import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Download, Search, UserRound, Building2, Briefcase, Clock, CheckCircle2, GraduationCap } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/utils/api';
import { errMsg, QUALIFICATIONS, INDUSTRIES, CANDIDATE_EXPERIENCE_TYPES } from '@/components/jobfairs/fairApi';
import { StatusBadge } from './JobFairList';
import { ProfileSheet } from './ProfileSheet';
import { downloadBlob, FileLink, fmtDate, EmpStatus, EMP_STATUS, show } from './regUtils';

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white rounded-xl border p-4" data-testid={testId}>
    <Icon className="w-5 h-5 text-teal-600 mb-2" /><p className="text-2xl font-bold">{value ?? 0}</p><p className="text-xs text-slate-500">{label}</p>
  </div>
);

const Filter = ({ value, onChange, options, placeholder, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-md border px-3 text-sm bg-white max-w-[14rem]" data-testid={testId}>
    <option value="">{placeholder}</option>
    {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
  </select>
);

const COLS = {
  candidate: [['Reg. ID', (r) => <span className="font-mono text-xs">{r.registration_id}</span>], ['Name', (r) => r.full_name], ['Mobile', (r) => r.mobile],
    ['Qualification', (r) => show(r.course || r.highest_qualification)], ['Experience', (r) => show(r.experience_type)], ['Location', (r) => show([r.city, r.district].filter(Boolean).join(', '))]],
  employer: [['Reg. ID', (r) => <span className="font-mono text-xs">{r.registration_id}</span>], ['Company', (r) => r.company_name], ['HR', (r) => `${r.hr_name || ''} · ${r.hr_mobile}`],
    ['Industry', (r) => show(r.industry === 'Other' ? r.industry_other : r.industry)], ['Vacancies', (r) => show(r.total_vacancies)], ['Status', (r) => <EmpStatus s={r.status} testId={`employer-status-${r.registration_id}`} />]],
};

const RegTable = ({ fairId, kind, reloadKey, onOpen }) => {
  const [q, setQ] = useState('');
  const [f1, setF1] = useState('');
  const [f2, setF2] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const isEmp = kind === 'employer';

  const load = useCallback(() => {
    const params = { search: q || undefined, page, ...(isEmp ? { status: f1 || undefined, industry: f2 || undefined } : { experience_type: f1 || undefined, qualification: f2 || undefined }) };
    api.get(`/job-fairs/admin/${fairId}/${kind}s`, { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [fairId, kind, q, f1, f2, page, isEmp]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reloadKey]);

  return (
    <div className="bg-white border rounded-xl" data-testid={`${kind}-registrations-table`}>
      <div className="p-4 flex flex-wrap gap-2 items-center border-b">
        <div className="relative flex-1 min-w-[14rem]"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, mobile, reg. ID…" className="pl-9" data-testid={`${kind}-search-input`} /></div>
        {isEmp ? <>
          <Filter value={f1} onChange={(v) => { setF1(v); setPage(1); }} placeholder="All statuses" options={Object.entries(EMP_STATUS).map(([k, [l]]) => [k, l])} testId="employer-status-filter" />
          <Filter value={f2} onChange={(v) => { setF2(v); setPage(1); }} placeholder="All industries" options={INDUSTRIES} testId="employer-industry-filter" />
        </> : <>
          <Filter value={f1} onChange={(v) => { setF1(v); setPage(1); }} placeholder="All experience types" options={CANDIDATE_EXPERIENCE_TYPES} testId="candidate-experience-filter" />
          <Filter value={f2} onChange={(v) => { setF2(v); setPage(1); }} placeholder="All qualifications" options={QUALIFICATIONS} testId="candidate-qualification-filter" />
        </>}
        <Button variant="outline" onClick={() => downloadBlob(`/job-fairs/admin/${fairId}/${kind}s/export`, `${kind}s.csv`)} data-testid={`${kind}-export-csv-button`}><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>{COLS[kind].map(([h]) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}<th className="px-4 py-2.5">Files</th><th className="px-4 py-2.5">Registered</th></tr></thead>
          <tbody className="divide-y">
            {data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid={`${kind}-empty`}>No registrations found</td></tr>}
            {data.items.map((r) => (
              <tr key={r.registration_id} className="hover:bg-teal-50/40 cursor-pointer" onClick={() => onOpen(r)} data-testid={`${kind}-row-${r.registration_id}`}>
                {COLS[kind].map(([h, fn]) => <td key={h} className="px-4 py-2.5">{fn(r)}</td>)}
                <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-3">{(isEmp ? [['id_proof', 'ID_Proof'], ['company_logo', 'Logo']] : [['resume', 'Resume'], ['photo', 'Photo']]).map(([k, l]) => r.files?.[k] && <FileLink key={k} fairId={fairId} file={r.files[k]} label={`${r.registration_id}_${l}`} testId={`${kind}-download-${k}-${r.registration_id}`} />)}</div>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 flex items-center justify-between text-sm text-slate-500 border-t">
        <span data-testid={`${kind}-total`}>{data.total} registration(s)</span>
        <div className="flex gap-2 items-center">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${kind}-prev-page`}>Prev</Button>
          <span>Page {page} / {data.pages}</span>
          <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid={`${kind}-next-page`}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export default function JobFairRegistrations() {
  const { id } = useParams();
  const [fair, setFair] = useState(null);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadStats = useCallback(() => api.get(`/job-fairs/admin/${id}/stats`).then((r) => setStats(r.data)).catch(() => {}), [id]);
  useEffect(() => { api.get(`/job-fairs/admin/${id}`).then((r) => setFair(r.data)).catch((e) => toast.error(errMsg(e))); loadStats(); }, [id, loadStats]);

  const onStatus = async (rec, status, notes) => {
    try {
      await api.put(`/job-fairs/admin/${id}/employers/${rec.registration_id}/status`, { status, notes });
      toast.success(`Marked ${EMP_STATUS[status][0]}`);
      setOpen({ ...open, rec: { ...rec, status, status_notes: notes } });
      setReloadKey((k) => k + 1); loadStats();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!fair) return <div className="p-8 text-slate-500">Loading…</div>;
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="jobfair-registrations-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild><Link to="/admin/jobfairs" data-testid="registrations-back-button"><ArrowLeft className="w-5 h-5" /></Link></Button>
        <div><div className="flex items-center gap-2"><h1 className="text-xl font-bold">{fair.name}</h1><StatusBadge fair={fair} /></div><p className="text-sm text-slate-500">Registrations</p></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat icon={UserRound} label="Candidates" value={stats.candidates} testId="reg-stat-candidates" />
        <Stat icon={GraduationCap} label="Freshers" value={stats.freshers} testId="reg-stat-freshers" />
        <Stat icon={Briefcase} label="Experienced" value={stats.experienced} testId="reg-stat-experienced" />
        <Stat icon={Building2} label="Employers" value={stats.employers} testId="reg-stat-employers" />
        <Stat icon={Clock} label="Under Verification" value={stats.employers_pending} testId="reg-stat-pending" />
        <Stat icon={CheckCircle2} label="Approved Employers" value={stats.employers_approved} testId="reg-stat-approved" />
        <Stat icon={Briefcase} label="Total Vacancies" value={stats.vacancies} testId="reg-stat-vacancies" />
      </div>
      <Tabs defaultValue="candidate">
        <TabsList><TabsTrigger value="candidate" data-testid="reg-tab-candidates">Job Seekers ({stats.candidates ?? 0})</TabsTrigger><TabsTrigger value="employer" data-testid="reg-tab-employers">Employers ({stats.employers ?? 0})</TabsTrigger></TabsList>
        <TabsContent value="candidate" className="mt-4"><RegTable fairId={id} kind="candidate" reloadKey={reloadKey} onOpen={(rec) => setOpen({ kind: 'candidate', rec })} /></TabsContent>
        <TabsContent value="employer" className="mt-4"><RegTable fairId={id} kind="employer" reloadKey={reloadKey} onOpen={(rec) => setOpen({ kind: 'employer', rec })} /></TabsContent>
      </Tabs>
      <ProfileSheet fairId={id} rec={open?.rec} kind={open?.kind} onClose={() => setOpen(null)} onStatus={onStatus} />
    </div>
  );
}
